// Notifications view — full-page list with kind filters.

import { css, html } from 'lit';
import { api } from '../../lib/api.js';
import { BaseComponent } from '../../lib/base-component.js';
import { router } from '../../lib/router.js';
import { notificationsState } from '../../state/notifications-state.js';
import { presenceState } from '../../state/presence-state.js';

class NotificationsView extends BaseComponent {
  static properties = {
    _filter: { type: String, state: true },
    _busyId: { type: String, state: true },
  };

  static styles = css`
    :host {
      display: block;
    }

    .header {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      flex-wrap: wrap;
      margin-bottom: 1rem;
    }

    .filter-btn {
      padding: 4px 12px;
      border-radius: 16px;
      border: 1px solid #ced4da;
      background: white;
      cursor: pointer;
      font-size: 0.85rem;
    }

    .filter-btn.active {
      background: #0d6efd;
      color: white;
      border-color: #0d6efd;
    }

    .row {
      display: flex;
      gap: 0.75rem;
      padding: 0.75rem 1rem;
      border: 1px solid #dee2e6;
      border-radius: 6px;
      margin-bottom: 0.5rem;
      align-items: flex-start;
    }

    .row.unread {
      background: #f0f7ff;
      border-color: #cfe2ff;
    }

    .row-content {
      flex: 1;
    }

    .row-actions {
      display: flex;
      gap: 6px;
    }

    .empty {
      text-align: center;
      padding: 2rem 1rem;
      color: #6c757d;
    }
  `;

  constructor() {
    super();
    /** @type {'all'|'friend_request'|'game_invitation'|'read'} */
    this._filter = 'all';
    this._busyId = '';
    /** @type {(() => void) | null} */
    this._unsubscribe = null;
  }

  async connectedCallback() {
    await super.connectedCallback();
    this._unsubscribe = notificationsState.subscribe(() => this.requestUpdate());
    notificationsState.refetch();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._unsubscribe?.();
    this._unsubscribe = null;
  }

  /** @param {'all'|'friend_request'|'game_invitation'|'read'} filter */
  _setFilter(filter) {
    this._filter = filter;
  }

  _filtered() {
    const all = notificationsState.notifications;
    switch (this._filter) {
      case 'friend_request':
        return all.filter((n) => n.kind === 'friend_request');
      case 'game_invitation':
        return all.filter((n) => n.kind === 'game_invitation');
      case 'read':
        return all.filter((n) => n.read);
      default:
        return all;
    }
  }

  async _markAllRead() {
    await notificationsState.markAllRead();
  }

  /** @param {string} requestId */
  async _accept(requestId) {
    this._busyId = requestId;
    try {
      await api.acceptFriendRequest(requestId);
      await notificationsState.refetch();
      presenceState.refetch();
    } finally {
      this._busyId = '';
    }
  }

  /** @param {string} requestId */
  async _reject(requestId) {
    this._busyId = requestId;
    try {
      await api.rejectFriendRequest(requestId);
      await notificationsState.refetch();
    } finally {
      this._busyId = '';
    }
  }

  /** @param {string} gameId */
  _join(gameId) {
    router.navigate(`/game/${gameId}/join`);
  }

  /** @param {object} n */
  _renderRow(n) {
    if (n.kind === 'friend_request' && n.friendRequest) {
      const fr = n.friendRequest;
      const isIncomingPending = fr.direction === 'incoming' && fr.status === 'pending';
      return html`
        <div class="row ${n.read ? '' : 'unread'}">
          <div class="row-content">
            <div>
              <strong>${fr.otherUsername}</strong>
              ${fr.direction === 'incoming' ? ' wants to be your friend' : ' — friend request sent'}
              ${fr.status !== 'pending' ? html` <span class="text-muted">(${fr.status})</span>` : ''}
            </div>
            <div class="text-muted small">${new Date(n.createdAt).toLocaleString()}</div>
          </div>
          ${
            isIncomingPending
              ? html`
                <div class="row-actions">
                  <button class="btn btn-sm btn-success" ?disabled="${this._busyId === fr.id}" @click="${() => this._accept(fr.id)}">Accept</button>
                  <button class="btn btn-sm btn-outline-secondary" ?disabled="${this._busyId === fr.id}" @click="${() => this._reject(fr.id)}">Decline</button>
                </div>
              `
              : ''
          }
        </div>
      `;
    }
    if (n.kind === 'game_invitation' && n.gameInvitation) {
      const gi = n.gameInvitation;
      return html`
        <div class="row ${n.read ? '' : 'unread'}">
          <div class="row-content">
            <div>
              <strong>${gi.inviterUsername}</strong> invited you to <em>${gi.gameAdventureName}</em>
              ${gi.status !== 'pending' ? html` <span class="text-muted">(${gi.status})</span>` : ''}
            </div>
            <div class="text-muted small">${new Date(n.createdAt).toLocaleString()}</div>
          </div>
          ${
            gi.status === 'pending'
              ? html`
                <div class="row-actions">
                  <button class="btn btn-sm btn-primary" @click="${() => this._join(gi.gameId)}">Join</button>
                </div>
              `
              : ''
          }
        </div>
      `;
    }
    return html`
      <div class="row ${n.read ? '' : 'unread'}">
        <div class="row-content">
          <div class="text-muted">Unknown notification (${n.kind})</div>
          <div class="text-muted small">${new Date(n.createdAt).toLocaleString()}</div>
        </div>
      </div>
    `;
  }

  render() {
    const items = this._filtered();
    return html`
      <div class="container py-4">
        <div class="d-flex align-items-center justify-content-between mb-3">
          <h2 class="h4 mb-0">Notifications</h2>
          <button class="btn btn-sm btn-outline-secondary" ?disabled="${notificationsState.unreadCount === 0}" @click="${this._markAllRead}">
            Mark all read
          </button>
        </div>
        <div class="header">
          <button class="filter-btn ${this._filter === 'all' ? 'active' : ''}" @click="${() => this._setFilter('all')}">All</button>
          <button class="filter-btn ${this._filter === 'friend_request' ? 'active' : ''}" @click="${() => this._setFilter('friend_request')}">Friend requests</button>
          <button class="filter-btn ${this._filter === 'game_invitation' ? 'active' : ''}" @click="${() => this._setFilter('game_invitation')}">Game invitations</button>
          <button class="filter-btn ${this._filter === 'read' ? 'active' : ''}" @click="${() => this._setFilter('read')}">Read</button>
        </div>
        ${
          items.length === 0
            ? html`<div class="empty">No notifications yet.</div>`
            : items.map((n) => this._renderRow(n))
        }
      </div>
    `;
  }
}

customElements.define('notifications-view', NotificationsView);
